import React from 'react'
import { DocumentNode } from 'graphql'
import { ViewerLoader } from '../viewer'
import { OrganizationType } from '../viewer/shared'
import {
  OrganizationCollectionLoader,
  CollectionVariables
} from '../organization-collection-loader'
import {
  AggregationsLoader,
  AggregationOptions,
  AggregationBucketIDConfigs,
  Aggregations
} from '../aggregations'
import { QuickFilter } from '../../legacy-design-system/quick-filter'
import { QuickFiltersLoader } from '../quick-filters-data'
import { Order } from '../../legacy-design-system/order'
import { Role } from '@contrast-security-inc/base-types'
import {
  SessionMetadataField,
  SessionMetadataFieldsLoader
} from '../session-metadata-field'
import {
  SessionMetadataAggregationLoader,
  SessionMetadataAggregation
} from '../session-metadata-aggregation'
import { GridDataRefetcher, RefreshGridDataFn } from './grid-data-refetcher'
import { PureQueryOptions } from 'apollo-client'

const initialLoadingRowCount = 5

interface Index {
  readonly index: number
}

interface LicenseBreakdown {
  readonly used: number
  readonly unused: number
}

interface ChildProps<
  TAggregationCategory extends string,
  TAdditionalCollectionProps,
  TData
> {
  readonly total?: number
  readonly totalRowCount: number
  readonly loadMore: () => Promise<any>
  readonly refresh: () => Promise<any>
  readonly organizationRole: Role
  readonly organizationID: string
  readonly organizationType: OrganizationType
  readonly restrictedEditRole?: boolean
  readonly assessLicenseBreakdown: LicenseBreakdown
  readonly protectLicenseBreakdown?: LicenseBreakdown
  readonly additionalCollectionProps: TAdditionalCollectionProps
  readonly collectionLoading: boolean
  readonly globalTotal?: number

  /**
   * True if the current user is a Protect user
   */
  readonly protect: boolean

  readonly quickFilters: QuickFilter[]
  readonly aggregations: Aggregations<TAggregationCategory>
  readonly sessionMetadataFields?: SessionMetadataField[]
  readonly sessionMetadataAggregations?: SessionMetadataAggregation[]
  readonly refetchQueries: PureQueryOptions[]
  readonly loadedRowCount: number
  readonly rowGetter: (params: Index) => any
  readonly rowIndexFinder: (
    predicate: (value: TData, index: number, obj: TData[]) => boolean
  ) => number
  readonly isRowLoaded: (params: Index) => boolean
  readonly rowDataValue: TData[]
  readonly refreshGridData: RefreshGridDataFn
}

interface Props<
  TOrderField extends string,
  TAggregationCategory extends string,
  TAdditionalCollectionProps,
  TData
> {
  // Query Configuration
  aggregationsQuery?: DocumentNode
  aggregationsQueries?: DocumentNode[]
  aggregationsQueryRootKey?: string
  quickFiltersQuery?: DocumentNode
  quickFiltersQueryRootKey?: string
  sessionMetadataFieldsQuery?: DocumentNode
  sessionMetadataFieldsQueryRootKey?: string
  sessionMetadataAggregationQuery?: DocumentNode
  collectionQuery: DocumentNode
  collectionQueryRootKey: string
  batchSize: number

  // Filters
  filterText: string
  order: Order<TOrderField>
  quickFilterID?: string
  applicationID?: string
  libraryHash?: string
  sessionMetadataFieldID?: string
  aggregationFilters?: AggregationOptions<TAggregationCategory>
  aggregationBucketIDConfigs?: AggregationBucketIDConfigs<TAggregationCategory>
  onSessionMetadataFieldIDChange?: (fieldID: string) => void
  additionalFilters?: { [key: string]: any }
  areVariablesEqual?: (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => boolean
  showQuickFilter?: (filter: QuickFilter) => boolean
  licensedOnly?: boolean
  isGroupBySinkEnabled?: boolean

  children: (
    props: ChildProps<TAggregationCategory, TAdditionalCollectionProps, TData>
  ) => React.ReactNode
}

export function GridLoader<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string = '',
  TAdditionalCollectionProps = {}
>({
  aggregationsQuery,
  aggregationsQueries,
  aggregationsQueryRootKey = '',
  batchSize,
  quickFiltersQuery,
  quickFiltersQueryRootKey,
  sessionMetadataFieldsQuery,
  sessionMetadataFieldsQueryRootKey,
  sessionMetadataAggregationQuery,
  collectionQuery,
  collectionQueryRootKey,
  filterText,
  order,
  quickFilterID,
  applicationID,
  libraryHash,
  sessionMetadataFieldID,
  aggregationFilters,
  onSessionMetadataFieldIDChange,
  additionalFilters,
  areVariablesEqual,
  showQuickFilter,
  licensedOnly,
  isGroupBySinkEnabled,
  children
}: Props<
  TOrderField,
  TAggregationCategory,
  TAdditionalCollectionProps,
  TData
>) {
  return (
    <ViewerLoader>
      {({ viewer, loading, queryInfo: viewerQueryInfo }) => {
        return (
          !loading &&
          !!viewer && (
            <OrganizationCollectionLoader<
              TData,
              TOrderField,
              TAggregationCategory,
              TAdditionalCollectionProps
            >
              query={collectionQuery}
              organizationID={viewer.currentOrganization.id}
              dataKey={collectionQueryRootKey}
              limit={batchSize}
              order={order}
              filterText={filterText}
              aggregationFilters={aggregationFilters}
              quickFilterID={quickFilterID}
              applicationID={applicationID}
              libraryHash={libraryHash}
              additionalFilters={additionalFilters}
              areVariablesEqual={areVariablesEqual}
              licensedOnly={licensedOnly}
              isGroupBySinkEnabled={isGroupBySinkEnabled}
            >
              {({
                loadMore,
                items,
                total,
                globalTotal,
                loading: collectionLoading,
                refresh,
                additionalProps: additionalCollectionProps,
                apolloClient
              }) => {
                return (
                  <AggregationsLoader
                    queries={
                      !!aggregationsQueries
                        ? aggregationsQueries
                        : !!aggregationsQuery
                        ? [aggregationsQuery]
                        : []
                    }
                    queryRootKey={aggregationsQueryRootKey}
                    organizationID={viewer.currentOrganization.id}
                    filterText={filterText}
                    quickFilter={quickFilterID}
                    licensedOnly={licensedOnly}
                    aggregationFilters={aggregationFilters}
                    additionalFilters={additionalFilters}
                    areVariablesEqual={areVariablesEqual}
                  >
                    {({ queryInfos: aggregationsQueryInfos, aggregations }) => {
                      return (
                        <QuickFiltersLoader
                          query={quickFiltersQuery}
                          queryRootKey={quickFiltersQueryRootKey}
                          organizationID={viewer.currentOrganization.id}
                          applicationID={applicationID}
                          filterText={filterText}
                          aggregationFilters={aggregationFilters}
                          licensedOnly={licensedOnly}
                          additionalFilters={additionalFilters}
                          areVariablesEqual={areVariablesEqual}
                        >
                          {({
                            quickFilters,
                            queryInfo: quickFilterQueryInfo
                          }) => {
                            let totalRowCount = total
                            if (collectionLoading) {
                              const quickFilterIndex = quickFilters.findIndex(
                                (f) => f.id === quickFilterID
                              )
                              const quickFilterTotal =
                                quickFilterIndex > -1
                                  ? quickFilters[quickFilterIndex].count
                                  : 0

                              totalRowCount = quickFilterTotal
                                ? quickFilterTotal
                                : initialLoadingRowCount
                            }

                            if (!!showQuickFilter) {
                              quickFilters =
                                quickFilters.filter(showQuickFilter)
                            }

                            const refetchQueries = [
                              ...aggregationsQueryInfos,
                              quickFilterQueryInfo,
                              viewerQueryInfo
                            ].filter((query) => query !== undefined)

                            const organizationType =
                              viewer.currentOrganization.type
                            return (
                              <GridDataRefetcher
                                apolloClient={apolloClient}
                                refetchQueries={
                                  refetchQueries as PureQueryOptions[]
                                }
                                refresh={refresh}
                              >
                                {({ refreshGridData }) => {
                                  let childrenProps = {
                                    total,
                                    totalRowCount,
                                    globalTotal,
                                    loadMore: () =>
                                      collectionLoading
                                        ? Promise.resolve()
                                        : loadMore(),
                                    refresh,
                                    organizationID:
                                      viewer.currentOrganization.id,
                                    organizationType,
                                    restrictedEditRole:
                                      !!viewer.currentOrganization
                                        .restrictedEditRole,
                                    organizationRole:
                                      viewer.currentOrganization.role,
                                    assessLicenseBreakdown:
                                      viewer.currentOrganization
                                        .assessLicenseBreakdown,
                                    protectLicenseBreakdown:
                                      viewer.currentOrganization
                                        .protectLicenseBreakdown,
                                    protect: viewer.protect,
                                    quickFilters,
                                    aggregations,
                                    refetchQueries:
                                      refetchQueries as PureQueryOptions[],
                                    isRowLoaded: ({ index }) =>
                                      items.length > 0 && index < items.length,
                                    rowGetter: ({ index }) =>
                                      items[index] || {},
                                    rowIndexFinder: (find) =>
                                      items.findIndex(find),
                                    loadedRowCount: items.length,
                                    rowDataValue: items,
                                    additionalCollectionProps,
                                    collectionLoading,
                                    refreshGridData
                                  }

                                  const isFreemiumOrganization =
                                    organizationType === 'Freemium'

                                  return !!sessionMetadataFieldsQuery &&
                                    !!sessionMetadataFieldsQueryRootKey &&
                                    !!aggregationFilters &&
                                    !isFreemiumOrganization ? ( // fremium organizations don't handle multiple sessions
                                    <SessionMetadataFieldsLoader
                                      query={sessionMetadataFieldsQuery}
                                      queryRootKey={
                                        sessionMetadataFieldsQueryRootKey
                                      }
                                      organizationID={
                                        viewer.currentOrganization.id
                                      }
                                      areVariablesEqual={areVariablesEqual}
                                    >
                                      {({
                                        sessionMetadataFields,
                                        queryInfo: sessionMetadataFieldQueryInfo
                                      }) => {
                                        if (
                                          !sessionMetadataFieldID &&
                                          Array.isArray(
                                            sessionMetadataFields
                                          ) &&
                                          sessionMetadataFields.length > 0 &&
                                          onSessionMetadataFieldIDChange
                                        ) {
                                          onSessionMetadataFieldIDChange(
                                            sessionMetadataFields[0].id
                                          )
                                        }

                                        const sessionMetadataFieldsChildrenProps =
                                          {
                                            sessionMetadataFields,
                                            refetchQueries: [
                                              ...(refetchQueries as PureQueryOptions[]),
                                              sessionMetadataFieldQueryInfo
                                            ]
                                          }

                                        childrenProps = {
                                          ...childrenProps,
                                          ...sessionMetadataFieldsChildrenProps
                                        }

                                        return sessionMetadataAggregationQuery ? (
                                          <SessionMetadataAggregationLoader
                                            query={
                                              sessionMetadataAggregationQuery
                                            }
                                            organizationID={
                                              viewer.currentOrganization.id
                                            }
                                            quickFilter={quickFilterID}
                                            filterText={filterText}
                                            aggregationFilters={
                                              aggregationFilters
                                            }
                                            additionalFilters={
                                              additionalFilters
                                            }
                                          >
                                            {({
                                              queryInfo:
                                                sessionMetadataAggregationQueryInfo,
                                              sessionMetadataAggregations
                                            }) => {
                                              const sessionMetadataAggregationsChildrenProps =
                                                {
                                                  sessionMetadataAggregations,
                                                  refetchQueries: [
                                                    ...(refetchQueries as PureQueryOptions[]),
                                                    sessionMetadataAggregationQueryInfo
                                                  ]
                                                }

                                              childrenProps = {
                                                ...childrenProps,
                                                ...sessionMetadataAggregationsChildrenProps
                                              }

                                              return children({
                                                ...childrenProps
                                              })
                                            }}
                                          </SessionMetadataAggregationLoader>
                                        ) : (
                                          children({ ...childrenProps })
                                        )
                                      }}
                                    </SessionMetadataFieldsLoader>
                                  ) : (
                                    children({ ...childrenProps })
                                  )
                                }}
                              </GridDataRefetcher>
                            )
                          }}
                        </QuickFiltersLoader>
                      )
                    }}
                  </AggregationsLoader>
                )
              }}
            </OrganizationCollectionLoader>
          )
        )
      }}
    </ViewerLoader>
  )
}
